// Singleton bridge between the blueprint face-graph nodes and the Isaac
// inference/render engine owned by OGSModel.jsx. A module-level singleton,
// never React state, safe to import from plugin code and the R3F component
// alike. Lives under src/ogs/render/ so it ships with the rest of the
// Isaac pipeline.
//
// The data path is a source -> drive -> engine chain, so the editor blueprint's
// Isaac Splat node is a *real* runtime dependency, not just a visual:
//
//   face graph (OGSModel.driveSplatFace)  -> setRigDirect(rig)        all three hops at once
//   Isaac Splat node tick                 -> pumpDriveToEngine()      runFull(drive) on change
//   camera orbit (OGSModel, debounced)    -> runAppearance()          appearance-only refresh
//
// OGSModel installs the engine via attachEngine({ runFull, runAppearance }).
// Disabling either node in the editor breaks the chain (frozen splats), which
// is the point — the nodes drive the mesh.
//
// All the per-frame `tick` entry points are idempotent via sequence counters:
// GraphEvaluator ticks perFrame plugin nodes both in the sideband loop AND via
// in-graph traversal, so each may fire twice per frame. The second call is a
// no-op.

// Default rig size until an engine declares its own (attachEngine rigDim).
// Isaac V1 is 60 controls; the V2 head+teeth engine declares 174 — the buffers
// resize on attach so setRig never truncates a full-space rig.
const DEFAULT_RIG_DIM = 60

class IsaacSplatHandle {
  constructor() {
    this._engine = null
    this._source = new Float32Array(DEFAULT_RIG_DIM)
    this._drive = new Float32Array(DEFAULT_RIG_DIM)
    this._sourceSeq = 0
    this._forwardedSeq = -1 // last _sourceSeq the source node forwarded
    this._driveSeq = 0
    this._isaacSeq = -1 // last _driveSeq the isaac node pumped to the engine
    this._attachListeners = new Set()
    this._rendererNode = null // terminal OGSRenderer, published by the ogs-renderer plugin factory
  }

  // --- Engine wiring (OGSModel owns the heavy runner/lifter/renderer) ---

  // Register a callback that fires when the engine attaches (or immediately if
  // it's already attached).
  onAttach(cb) {
    this._attachListeners.add(cb)
    if (this._engine) cb(this)
    return () => this._attachListeners.delete(cb)
  }

  // engine: { runFull(rig: Float32Array): Promise, runAppearance(): void,
  //           rigDim?: number }
  attachEngine(engine) {
    this._engine = engine
    // Adopt the engine's control-space size: a 174-dim head+teeth rig pushed
    // through 60-length buffers would be silently truncated (and then ignored
    // by the engine's dim guard) — resize BEFORE the attach listeners and the
    // pending-rig pump so everything downstream sees the right length.
    const dim = Number.isInteger(engine?.rigDim) && engine.rigDim > 0 ? engine.rigDim : null
    if (dim && dim !== this._source.length) {
      const src = this._source, drv = this._drive
      this._source = new Float32Array(dim)
      this._drive = new Float32Array(dim)
      this._source.set(src.subarray(0, Math.min(dim, src.length)))
      this._drive.set(drv.subarray(0, Math.min(dim, drv.length)))
    }
    for (const cb of this._attachListeners) {
      try { cb(this) } catch (e) { console.error('[isaacSplat.onAttach]', e) }
    }
    // Pump whatever rig is already pending so splats render on first attach.
    this._isaacSeq = -1
    this.pumpDriveToEngine()
  }

  detachEngine() { this._engine = null; this._rendererNode = null }
  isReady() { return !!this._engine }

  // --- Terminal render node (its lifter/updater live and die with the engine) ---
  setRendererNode(node) { this._rendererNode = node }
  getRendererNode() { return this._rendererNode }

  // --- Rig source (face-graph lip-sync, console pokes) ---
  setRig(rig) {
    if (!rig) return
    // Grow to fit a larger control space (e.g. a 174-dim rig arriving before
    // the engine attach declared its rigDim) — never truncate a full rig. A
    // SHORTER rig only writes its prefix, same as before.
    if (rig.length > this._source.length) {
      const src = this._source, drv = this._drive
      this._source = new Float32Array(rig.length)
      this._drive = new Float32Array(rig.length)
      this._source.set(src)
      this._drive.set(drv)
    }
    const n = Math.min(rig.length, this._source.length)
    for (let i = 0; i < n; i++) this._source[i] = rig[i]
    this._sourceSeq++
  }
  getSourceRig() { return this._source }

  // --- Forward source -> drive. Returns true when a new rig was forwarded. ---
  forwardSourceToDrive() {
    if (this._forwardedSeq === this._sourceSeq) return false
    this._drive.set(this._source)
    this._driveSeq++
    this._forwardedSeq = this._sourceSeq
    return true
  }

  // --- Isaac Splat node: pump drive -> engine (runs the inference pipeline) ---
  // Returns true when a fresh rig was handed to the engine this frame.
  pumpDriveToEngine() {
    if (!this._engine) return false
    if (this._isaacSeq === this._driveSeq) return false
    this._isaacSeq = this._driveSeq
    // Defensive copy — the engine's async pipeline must never see _drive mutate.
    Promise.resolve(this._engine.runFull(new Float32Array(this._drive)))
      .catch(e => console.error('[isaacSplat] runFull failed', e))
    return true
  }

  // --- Camera-only appearance refresh (OGSModel debounced orbit listener) ---
  runAppearance() { this._engine?.runAppearance?.() }

  // --- Opacity-mask cull (authoring-time) ---
  // Install a UV-space occlusion map (or null to clear) on the lifter and
  // re-lift so occluded splats are dropped. Forwarded to the engine, which owns
  // the lifter; a no-op until the engine attaches. Mask edits are rare, so the
  // re-lift cost (re-runs appr) is irrelevant — matches the doc decision in
  // SPLATS-MATERIAL.md (Task 3).
  setOpacityMask(mask) { this._engine?.setOpacityMask?.(mask ?? null) }

  // --- Blueprint renderer-node gate: show/hide the presented splat ---
  setSplatVisible(v) { this._engine?.setSplatVisible?.(v) }

  // Console/debug + initial-kick helper: set the rig and push it all the way
  // through the chain in one call, bypassing the per-frame node ticks.
  setRigDirect(rig) {
    this.setRig(rig)
    this.forwardSourceToDrive()
    this.pumpDriveToEngine()
  }
}

export const isaacSplat = new IsaacSplatHandle()

if (typeof window !== 'undefined') {
  window.__ISAAC_SPLAT__ = isaacSplat
}
